import { useRef } from 'react'
import qq from 'fine-uploader'
import _ from 'lodash/fp'
import { isDev, FileState, getFileNameAndExtension, tryReadingFile } from '../utils'
import { useConstant } from '@startlibs/core'
import {UploaderOrigin} from '../../../config/UploaderOrigin'
import { syncControlLock } from './queues'

const getFinalValue = maybeFn => _.isFunction(maybeFn) ? getFinalValue(maybeFn()) : maybeFn

const createUploader = (fileInstanceToUpload, params, getJwt, incrementProgress) => {
  const { file: fileToUpload, params: fileParams } = fileInstanceToUpload
  const [filename, extension] = getFileNameAndExtension(fileToUpload)
  let isFirstProgress = true
  let lastProgress = 0
  let resolve = _.identity
  let reject = _.identity
  const promise = new Promise((res, rej) => {
    resolve = res
    reject = rej
  })

  const uploader = new qq.FineUploaderBasic({
    autoUpload: true,
    maxConnections: 2,
    // debug: true,
    request: {
      requireSuccessJson: false,
      endpoint: UploaderOrigin() + '/uploads',
      filenameParam: 'filename',
      params: {
        ...params,
        ...fileParams,
        qqfilename: filename + (extension ? '.' : '') + extension,
      },
      customHeaders: {
        Authorization: `Bearer ${getFinalValue(getJwt)}`,
      },
    },
    retry: {
      enableAuto: true,
      maxAutoAttempts: isDev ? 1 : 5,
    },
    chunking: {
      enabled: true,
      concurrent: {
        enabled: true,
      },
      success: {
        endpoint: UploaderOrigin() + '/chunksdone',
        customHeaders: {
          Authorization: `Bearer ${getFinalValue(getJwt)}`,
        },
      },
      partSize: 2e6,
    },

    callbacks: {
      onAllComplete: (success, failed) => {
        if (uploader.idempotentError) { return; }
        if (failed.length) {
          console.log('Upload completed: ', fileToUpload.name)
        }
      },
      onProgress: (id, name, uploaded, total) => {
        uploader.setCustomHeaders({ Authorization: `Bearer ${getFinalValue(getJwt)}` })
        if (isFirstProgress) {
          isFirstProgress = false
        } else {
          const delta = uploaded - lastProgress
          lastProgress = uploaded
          incrementProgress(delta)
        }
      },
      onCancel: () => {
        reject({ error: FileState.Canceled, progressDelta: -lastProgress })
      },
      onComplete: (id, fileName, responseJSON, xhr) => {
        if (!xhr) {
          const progressDelta = -lastProgress
          reject({ error: FileState.ProcessingError, progressDelta })
          return
        }
        if (!responseJSON.success && xhr?.status !== 203) {
          const progressDelta = -lastProgress
          lastProgress = 0
          if (xhr.status > 0) {
            if (xhr.status === 406) {
              uploader.idempotentError = true
              reject({ error: FileState.InvalidFilename, progressDelta })
            } else {
              reject({ error: FileState.ProcessingError, progressDelta })
            }
          } else {
            tryReadingFile(fileToUpload).then(
              () => reject({ error: FileState.ConnectionError, progressDelta }),
              () => reject({ error: FileState.FileNotFound, progressDelta })
            )
          }
        } else {
          const progressDelta = fileToUpload.size - lastProgress
          lastProgress = fileToUpload.size
          if (xhr?.status === 203) {
            // Custom response status - Virus found
            uploader.hasBeenQuarantined = true
            resolve({ ...responseJSON, quarantined: true })
          } else {
            incrementProgress(progressDelta)
            resolve(responseJSON)
          }
        }
      },
      onError: (id,filename,reason,xhr) => {
        const progressDelta = -lastProgress
        if (xhr?.status === 406) {
          uploader.idempotentError = true
          reject({ error: FileState.InvalidFilename, progressDelta, errorMessage: reason })
          if (uploader._options?.retry?.enableAuto) {
            uploader._options.retry.enableAuto = false
          }
        }
      },
      onUpload: () => {
        if (uploader.hasBeenQuarantined || uploader.idempotentError) {
          return Promise.reject()
        }
        return Promise.resolve()
      },
      onAutoRetry: (id, name) => {
        if (uploader.idempotentError) { return; }
        console.log('Auto retry event: ', name)
        uploader.setCustomHeaders({ Authorization: `Bearer ${getFinalValue(getJwt)}` })
      },
      onManualRetry: (id, name) => {
        console.log('Manual retry event: ', name)
        uploader.setCustomHeaders({ Authorization: `Bearer ${getFinalValue(getJwt)}` })
      },
      onUploadChunk: () => {
        uploader.setCustomHeaders({ Authorization: `Bearer ${getFinalValue(getJwt)}` })
      },
    },
  })
  console.log('Upload starting: ', fileToUpload.name)

  uploader.addFiles([fileToUpload])

  return [promise, uploader]
}

export const useUploader = (getJwt, queue, recordManager, getMode) => {
  const isActive = useRef()
  const smallFileActive = useRef()
  const activeUploads = useRef([])

  const uploadNextFile = async () => {

    // if(getMode('slowMode')){
    //   const {
    //     value: [unlock, hasSlots],
    //   } = await queue.next()

    //   // Wait for control lock release before processing files
    //   const {
    //     value: [unlockControl],
    //   } = await syncControlLock.next()
    //   // if (isPaused.current == true){
    //   //   unlockControl()
    //   //   unlock()
    //   //   if (hasFile) {
    //   //     uploadNextFile()
    //   //   }
    //   //   return
    //   // }

    //   const isSmallestFile = smallFileActive.current
    //   let hasFile = false
    //   try {
    //     const [fileInstance, recordKey, params] = await recordManager.consumeFile(isSmallestFile)
    //     if (!fileInstance) {
    //       isActive.current = false
    //       return
    //     }
    //     hasFile = true
    //     if (!isSmallestFile) {
    //       smallFileActive.current = true
    //     }
    //     const [uploaderPromise, uploaderInstance] = createUploader(
    //       fileInstance,
    //       params,
    //       getJwt,
    //       recordManager.incrementProgress(recordKey)
    //     )
    //     let resolveCompletedUpload = _.identity
    //     const completeUploadPromise = new Promise(res => {
    //       resolveCompletedUpload = res
    //     })
    //     activeUploads.current = activeUploads.current.concat([
    //       {
    //         file: fileInstance.file,
    //         recordKey,
    //         uploaderInstance,
    //         uploadPromise: completeUploadPromise,
    //       },
    //     ])

    //     if (hasSlots) {
    //       uploadNextFile()
    //     }
    //     try {
    //       const response = await uploaderPromise
    //       const recordUID = await recordManager.uploadSuccess(recordKey, fileInstance, response)
    //       resolveCompletedUpload(recordUID)
    //     } catch (e) {
    //       await recordManager.uploadFailure(recordKey, fileInstance, e.error, e.progressDelta, e.errorMessage)
    //     } finally {
    //       resolveCompletedUpload()
    //       activeUploads.current = _.differenceBy('file', activeUploads.current, [fileInstance])
    //     }
    //   } finally {
    //     // release control lock
    //     unlockControl()
    //     // this code below deals with files to be uploaded queue
    //     unlock()
    //     if (hasFile) {
    //       uploadNextFile()
    //     }
    //   }
    // } else {
      const {
        value: [unlock, hasSlots],
      } = await queue.next()
      const isSmallestFile = smallFileActive.current
      let hasFile = false
      try {
        const [fileInstance, recordKey, params] = await recordManager.consumeFile(isSmallestFile)
        if (!fileInstance) {
          isActive.current = false
          return
        }
        hasFile = true
        if (!isSmallestFile) {
          smallFileActive.current = true
        }
        const [uploaderPromise, uploaderInstance] = createUploader(
          fileInstance,
          params,
          getJwt,
          recordManager.incrementProgress(recordKey)
        )
        let resolveCompletedUpload = _.identity
        const completeUploadPromise = new Promise(res => {
          resolveCompletedUpload = res
        })
        activeUploads.current = activeUploads.current.concat([
          {
            file: fileInstance.file,
            recordKey,
            uploaderInstance,
            uploadPromise: completeUploadPromise,
          },
        ])

        if (hasSlots) {
          uploadNextFile()
        }
        try {
          const response = await uploaderPromise
          const recordUID = await recordManager.uploadSuccess(recordKey, fileInstance, response)
          resolveCompletedUpload(recordUID)
        } catch (e) {
          await recordManager.uploadFailure(recordKey, fileInstance, e.error, e.progressDelta, e.errorMessage)
        } finally {
          resolveCompletedUpload()
          activeUploads.current = _.differenceBy('file', activeUploads.current, [fileInstance])
        }
      } finally {
        unlock()
        if (hasFile) {
          uploadNextFile()
        }
      }
    // }
  }

  return useConstant({
    start: () => {
      if (isActive.current) {
        return
      }
      isActive.current = true
      uploadNextFile()
    },
    cancelRecord: async recordKey => {
      const [toCancel, toKeep] = _.partition(['recordKey', recordKey], activeUploads.current)
      activeUploads.current = toKeep
      const promises = toCancel.map(_.get('uploadPromise'))
      toCancel.forEach(({ uploaderInstance }) => uploaderInstance.cancelAll())
      const results = await Promise.all(promises)
      return results.find(Boolean)
    },
  })
}
